/**
 * WebhookFormBase - Base class for forms that submit to Flow Webhooks
 * Provides common functionality for form handling, validation, and submission
 *
 * Subclasses must implement:
 * - buildPayload(): Return the webhook payload object
 * - resetForm(): Reset form fields after submission
 * - validateCustomFields(): Optional custom validation (return true by default)
 * - getSuccessMessage(): Optional custom success message
 */
class WebhookFormBase {
  /**
   * @param {Object} config - Form configuration
   * @param {string} config.formId - Form element ID
   * @param {string} config.submitButtonId - Submit button element ID
   * @param {string} config.successMessageId - Success message element ID
   * @param {string} config.errorMessageId - Error message element ID
   * @param {string} config.errorTextId - Error text element ID
   * @param {string} config.loadingAnnouncementId - Loading announcement element ID for screen readers
   * @param {string} config.configKey - Window object key for webhook configuration
   * @param {string} config.translationsKey - Window object key for translations
   */
  constructor(config) {
    // Get form elements
    this.form = document.getElementById(config.formId);
    if (!this.form) return;

    // Defensive guard for SHOPIFY-1SQ: webhook-form-utils.js loads first via
    // script_tag in quote-request-form-modal.liquid / return-request-form.liquid,
    // but if that script gets truncated or blocked mid-evaluation the global
    // never lands and this constructor throws an unhandled ReferenceError.
    // Fail soft with a clear console signal instead of crashing the page.
    if (typeof WebhookFormUtils === 'undefined' || typeof FormUIUtils === 'undefined') {
      console.warn(
        '[WebhookFormBase] Skipping init for form "' + config.formId +
        '": WebhookFormUtils / FormUIUtils not loaded (likely a script load failure on webhook-form-utils.js).',
      );
      this.disabled = true;
      // Leave the object in a usable shape: subclasses run code after super()
      // that touches this.translations before they can check this.disabled, so
      // initialize it here to avoid a TypeError on the soft-fail path. See SHOPIFY-1SQ.
      this.translations = {};
      return;
    }

    this.submitButton = document.getElementById(config.submitButtonId);
    this.successMessage = document.getElementById(config.successMessageId);
    this.errorMessage = document.getElementById(config.errorMessageId);
    this.errorText = document.getElementById(config.errorTextId);
    this.loadingAnnouncement = document.getElementById(config.loadingAnnouncementId);

    // Initialize shared utilities
    this.webhookUtils = WebhookFormUtils.parseConfig(config.configKey);
    this.uiUtils = new FormUIUtils(
      this.submitButton,
      this.successMessage,
      this.errorMessage,
      this.errorText
    );

    // Load translations
    this.loadTranslations(config.translationsKey);

    // Setup real-time validation
    this.uiUtils.setupRealtimeValidation(this.form);

    // Submission state
    this.isSubmitting = false;
  }

  /**
   * Load translations from window object
   * @param {string} translationsKey - Window object key for translations
   */
  loadTranslations(translationsKey) {
    this.translations = window[translationsKey] || {};
  }

  /**
   * Get translated string
   * @param {string} key - Translation key
   * @param {Object} replacements - Optional key-value pairs for string interpolation
   * @returns {string} Translated string
   */
  getTranslation(key, replacements = {}) {
    let text = this.translations[key] || key;

    // Replace placeholders like {count} with actual values
    Object.keys(replacements).forEach(placeholder => {
      text = text.replace(`{${placeholder}}`, replacements[placeholder]);
    });

    return text;
  }

  /**
   * Bind form submission event
   * Subclasses can override to add additional event bindings
   */
  bindEvents() {
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleSubmit();
      });
    }
  }

  /**
   * Check honeypot field for spam detection
   * @returns {boolean} True if honeypot is empty (not a bot), false if filled (likely bot)
   */
  validateHoneypot() {
    const honeypot = this.form.querySelector('[data-honeypot]');
    if (honeypot && honeypot.value) {
      // Silently fail - don't give bots feedback
      console.warn('Honeypot triggered - submission blocked');
      return false;
    }
    return true;
  }

  /**
   * Handle form submission
   * This is the main submission flow - subclasses typically don't need to override this
   */
  async handleSubmit() {
    if (this.isSubmitting) return;

    // Check honeypot first - silently reject bot submissions
    if (!this.validateHoneypot()) {
      // Fake success to not alert bots
      this.uiUtils.showMessage('success', this.getSuccessMessage(), {
        autoCloseDelay: 0,
        scrollIntoView: true
      });
      return;
    }

    try {
      this.isSubmitting = true;
      this.uiUtils.setButtonLoading(true);
      this.uiUtils.hideMessage();

      // Announce loading state to screen readers
      if (this.loadingAnnouncement && this.translations.submitting_announcement) {
        this.loadingAnnouncement.textContent = this.getTranslation('submitting_announcement');
      }
      if (this.submitButton) {
        this.submitButton.setAttribute('aria-busy', 'true');
      }

      // Validate form using shared utility
      if (!this.uiUtils.validateForm(this.form)) {
        return;
      }

      // Validate custom fields (implemented by subclass)
      if (!this.validateCustomFields()) {
        return;
      }

      // Build payload (implemented by subclass)
      const payload = this.buildPayload();

      // Send to webhook using shared utility
      const response = await this.webhookUtils.submitToWebhook(payload);

      if (response.ok) {
        this.uiUtils.showMessage('success', this.getSuccessMessage(), {
          autoCloseDelay: 0,
          scrollIntoView: true
        });
        this.resetForm();
      } else {
        const errorText = await response.text().catch(() => response.statusText);
        console.error('Response error:', errorText);
        throw new Error(`Failed to submit form (${response.status}). Please try again or contact support.`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);

      // Show email fallback if webhook is unreachable
      if (error.message && error.message.includes('Unable to connect')) {
        // Extract form data for email fallback
        const formData = new FormData(this.form);
        const formDataObject = {};

        for (const [key, value] of formData.entries()) {
          // Skip hidden system fields
          if (!key.startsWith('form_') && value) {
            formDataObject[key] = value;
          }
        }

        // Show email fallback with form data
        const subject = this.getEmailSubject ? this.getEmailSubject() : 'Form Submission Request';
        this.uiUtils.showEmailFallback(formDataObject, 'smile@lamps.com', subject);
      } else {
        // Show standard error message
        this.uiUtils.showMessage('error', error.message);
      }
    } finally {
      this.isSubmitting = false;
      this.uiUtils.setButtonLoading(false);

      // Clear loading announcement
      if (this.loadingAnnouncement) {
        this.loadingAnnouncement.textContent = '';
      }
      if (this.submitButton) {
        this.submitButton.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped HTML
   */
  escapeHtml(text) {
    if (text == null) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Escape string for use in HTML attributes (escapes quotes in addition to HTML entities)
   * Use this for value="..." attributes where double quotes need to be &quot;
   * @param {string} text - Text to escape
   * @returns {string} Escaped string safe for HTML attributes
   */
  escapeAttr(text) {
    if (!text) return '';
    return this.escapeHtml(text).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Abstract methods that subclasses MUST implement

  /**
   * Build webhook payload from form data
   * @returns {Object} Webhook payload object
   * @abstract
   */
  buildPayload() {
    throw new Error('buildPayload() must be implemented by subclass');
  }

  /**
   * Reset form after successful submission
   * @abstract
   */
  resetForm() {
    throw new Error('resetForm() must be implemented by subclass');
  }

  /**
   * Validate custom fields beyond basic required field validation
   * @returns {boolean} True if validation passes
   * @abstract
   */
  validateCustomFields() {
    return true; // Default implementation - no custom validation
  }

  /**
   * Get success message to display
   * @returns {string} Success message
   * @abstract
   */
  getSuccessMessage() {
    return 'Form submitted successfully!'; // Default message
  }
}

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.WebhookFormBase = WebhookFormBase;
}
